"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Search,
  ChevronDown,
  User,
  Heart,
  Globe,
  Menu,
  X,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MobileNavigation } from "@/components/mobile-navigation";
import { MegaMenu } from "@/components/mega-menu";
import { useScroll } from "@/lib/use-scroll";
import { useCommandPaletteContext } from "@/lib/command-palette-context";
import { QuoteCartDropdown } from "@/components/quote-cart-dropdown";
import { CurrencySelector } from "@/components/currency-selector";
import { cn } from "@/lib/utils";

// Type for grouped categories from API
interface GroupedCategory {
  name: string
  slug: string
  subcategories: Array<{
    id: number
    name: string
    slug: string
    productCount: number
  }>
  totalProducts: number
  icon: string
}

export function Header() {
  const { shouldShowNav, isScrolled } = useScroll();
  const { open, setOpen } = useCommandPaletteContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [megaMenuVisible, setMegaMenuVisible] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // State for categories from API
  const [categories, setCategories] = useState<GroupedCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Scrollable navigation refs and state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showScrollIndicators, setShowScrollIndicators] = useState(false);

  // Fetch categories from API on mount (use megaMenu=true for simplified navigation)
  useEffect(() => {
    fetch('/api/categories?megaMenu=true')
      .then(res => res.json())
      .then(data => {
        setCategories(data)
        setCategoriesLoading(false)
      })
      .catch(err => {
        console.error('Error fetching categories:', err)
        setCategoriesLoading(false)
      })
  }, [])

  // Check scroll position and update arrow visibility
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    setShowScrollIndicators(scrollWidth > clientWidth);
  };

  // Update scroll indicators when categories load
  useEffect(() => {
    if (!categoriesLoading && categories.length > 0) {
      setTimeout(checkScroll, 100);
    }
  }, [categoriesLoading, categories]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  // Scroll left/right
  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 300;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  // Clear any pending timeouts
  const clearTimeouts = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
  };

  // Open mega menu with slight delay
  const openMegaMenu = (categoryName: string) => {
    clearTimeouts();
    const timeout = setTimeout(() => {
      setActiveMegaMenu(categoryName);
      setMegaMenuVisible(true);
    }, 150);
    setHoverTimeout(timeout);
  };

  // Close mega menu with delay to allow mouse movement
  const closeMegaMenu = () => {
    clearTimeouts();
    const timeout = setTimeout(() => {
      setMegaMenuVisible(false);
      setActiveMegaMenu(null);
    }, 300);
    setCloseTimeout(timeout);
  };

  // Keep mega menu open
  const keepMegaMenuOpen = () => {
    clearTimeouts();
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition-all duration-300 delay-150",
        "bg-[#252626]",
        shouldShowNav ? "translate-y-0" : "-translate-y-full",
        isScrolled ? "shadow-md" : "shadow-none"
      )}
    >
      {/* Utility Bar */}
      <div className="bg-[#252626] text-slate-300 text-sm py-1.5 border-b border-slate-700">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex gap-4 items-center">
            {/* Show condensed links on small screens */}
            <div className="hidden md:flex gap-6">
              <Link href="/" className="hover:text-[#518BC4]">Home</Link>
              <Link href="/services" className="hover:text-[#518BC4]">Services</Link>
              <Link href="/industries" className="hover:text-[#518BC4]">Industries</Link>
              <Link href="/manufacturers" className="hover:text-[#518BC4]">Manufacturers</Link>
              <Link href="/about" className="hover:text-[#518BC4]">About</Link>
              <Link href="/careers" className="hover:text-[#518BC4]">Careers</Link>
              <Link href="/contact" className="hover:text-[#518BC4]">Contact</Link>
            </div>
            <div className="md:hidden text-slate-400 text-sm">&nbsp;</div>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="hidden sm:flex items-center gap-6">
              <Link href="/support" className="hover:text-[#518BC4]">Help</Link>
              <Link href="/quote/track" className="hover:text-[#518BC4]">Track Quote</Link>
            </div>
            <CurrencySelector />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:text-[#518BC4] outline-none">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">EN</span>
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>English</DropdownMenuItem>
                <DropdownMenuItem>Español</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className="bg-[#252626] text-white"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20 md:h-20 lg:h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link href="/">
                <Image
                  src="/prosoft-logo.webp"
                  alt="ProSoft Electronics"
                  width={150}
                  height={40}
                />
              </Link>
            </div>

            {/* Search Bar */}
            <div className="flex-1 px-4 sm:px-6 hidden lg:flex justify-center">
              <Button
                onClick={() => setOpen(true)}
                variant="outline"
                className="w-full max-w-2xl h-12 bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700 hover:border-slate-500 justify-start gap-3"
              >
                <Search className="w-5 h-5" />
                <span className="flex-1 text-left">
                  Search 6M+ products by part number, description...
                </span>
                <Kbd keys={["cmd", "k"]} className="ml-auto" />
              </Button>
            </div>

            {/* Action Icons */}
            <div className="hidden lg:flex items-center gap-6">
              <Link
                href="/wishlist"
                className="flex items-center gap-2 text-slate-200 hover:text-[#518BC4]"
              >
                <Heart className="w-6 h-6" />
                <span>Wishlist</span>
              </Link>

              <Link
                href="/quote"
                className="flex items-center gap-2 text-slate-200 hover:text-[#518BC4]"
              >
                <FileText className="w-6 h-6" />
                <span>Quote</span>
              </Link>
              <div className="flex items-center gap-2">
                <QuoteCartDropdown />
              </div>
            </div>

            {/* Mobile Menu Trigger */}
            <div className="lg:hidden flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(true)}
                className="hover:bg-slate-700"
                aria-label="Open search"
              >
                <Search className="w-6 h-6" />
              </Button>

              {/* Mobile Cart Icon */}
              <div className="lg:hidden">
                <QuoteCartDropdown />
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="hover:bg-slate-700"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Category Navigation - Scrollable */}
      <div
        className="bg-[#252626] border-t border-b border-slate-700 hidden lg:block relative"
        onMouseLeave={closeMegaMenu}
        onMouseEnter={keepMegaMenuOpen}
      >
        <div className="container mx-auto px-4 relative">
          {/* Left Scroll Button */}
          {showScrollIndicators && canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-0 bottom-0 z-10 w-12 bg-gradient-to-r from-[#252626] via-[#252626] to-transparent flex items-center justify-start pl-2 hover:from-slate-700"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6 text-slate-300" />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex items-center h-12 overflow-x-auto scrollbar-hide scroll-smooth"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {categoriesLoading ? (
              <div className="text-slate-400 text-sm mx-auto">Loading categories...</div>
            ) : (
              <div className="flex items-center">
                {categories.map((category) => (
                  <Button
                    key={category.name}
                    variant="ghost"
                    onMouseEnter={() => openMegaMenu(category.name)}
                    className={cn(
                      "h-full rounded-none text-slate-200 font-medium hover:bg-slate-700 hover:text-[#518BC4] transition-colors whitespace-nowrap flex-shrink-0 px-4",
                      activeMegaMenu === category.name &&
                        megaMenuVisible &&
                        "bg-slate-700 text-[#518BC4]"
                    )}
                  >
                    {category.name}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Right Scroll Button */}
          {showScrollIndicators && canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-0 bottom-0 z-10 w-12 bg-gradient-to-l from-[#252626] via-[#252626] to-transparent flex items-center justify-end pr-2 hover:from-slate-700"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6 text-slate-300" />
            </button>
          )}
        </div>

        {/* Mega Menu Dropdown */}
        {megaMenuVisible && activeMegaMenu && !categoriesLoading && (
          <div 
            className="absolute top-full left-0 w-full bg-white shadow-2xl border-t-4 border-[#518BC4] z-50"
            onMouseEnter={keepMegaMenuOpen}
            onMouseLeave={closeMegaMenu}
          >
            <div className="container mx-auto px-4 py-8">
              <MegaMenu
                category={
                  categories.find((cat) => cat.name === activeMegaMenu)!
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && <MobileNavigation />}
    </header>
  );
}